import axios from "axios";

import { api, apiBaseOrigin } from "../api";
import { clearAuthToken, getAuthToken } from "../utils/authToken";
import {
  clearPreviewAccessToken,
  getPreviewAccessToken,
} from "./utils/previewSession";

/**
 * Cliente só da anamnese v2. Preview Sanctum nunca entra no axios global do site
 * (pedidos, conta, catálogo). Sem sessão de preview, usa o token do cliente.
 */
const questionnaireApi = axios.create({
  baseURL: apiBaseOrigin,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

const questionnaireAccessToken = () =>
  getPreviewAccessToken() || getAuthToken();

questionnaireApi.interceptors.request.use((config) => {
  const token = questionnaireAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

questionnaireApi.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      if (getPreviewAccessToken()) {
        clearPreviewAccessToken();
      } else {
        clearAuthToken();
        localStorage.clear();
        window.location.href = "/";
      }
    }

    if (err?.response?.status === 404 && err?.response?.data?.message) {
      const msg = String(err.response.data.message).toLowerCase();
      if (
        msg.includes("membro da família não encontrado") ||
        msg.includes("persona não encontrada")
      ) {
        if (!getPreviewAccessToken()) {
          clearAuthToken();
          localStorage.clear();
          window.location.href = "/";
        }
      }
    }

    return Promise.reject(err);
  },
);

/**
 * Toda decisão de regra (visibilidade, validação, progresso) vem do servidor; aqui só transportamos.
 */

export const resolveProfile = async (payload) => {
  const { data } = await questionnaireApi.post("/persona-profiles/resolve", payload);
  return data?.data;
};

export const getProfileDraft = async (params = {}) => {
  const query = {};
  if (params.persona_id) query.persona_id = params.persona_id;
  if (params.persona_uuid) query.persona_uuid = params.persona_uuid;
  const { data } = await questionnaireApi.get("/persona-profiles/draft", { params: query });
  return data?.data;
};

export const getSubmission = async (uuid) => {
  const { data } = await questionnaireApi.get(`/questionnaire-submissions/${uuid}`);
  return data?.data;
};

export const getQuestionnaire = async (slug) => {
  const { data } = await questionnaireApi.get(`/questionnaires/${slug}`);
  return data?.data;
};

export const createSubmission = async (payload) => {
  const { data } = await questionnaireApi.post("/questionnaire-submissions", payload);
  return data?.data;
};

/**
 * Devolve sempre `{ status, data }`: no `intent=advance` o 422 é resposta esperada, com os erros
 * da seção, e não um erro de transporte.
 */
export const saveSection = async (uuid, sectionId, body) => {
  try {
    const { data } = await questionnaireApi.put(
      `/questionnaire-submissions/${uuid}/sections/${sectionId}/answers`,
      body,
    );
    return { status: 200, data: data?.data, errors: [] };
  } catch (error) {
    const response = error?.response;
    if (response?.status === 422) {
      return {
        status: 422,
        data: response.data?.data,
        errors: response.data?.errors ?? [],
      };
    }
    throw error;
  }
};

export const completeSubmission = async (uuid, extra = {}) => {
  const { data } = await questionnaireApi.post(
    `/questionnaire-submissions/${uuid}/complete`,
    extra,
  );
  return data?.data;
};

export const abandonSubmission = async (uuid) => {
  const { data } = await questionnaireApi.post(
    `/questionnaire-submissions/${uuid}/abandon`,
  );
  return data?.data;
};

export const listPersonaSubmissions = async (personaUuid) => {
  const { data } = await questionnaireApi.get(
    `/personas/${personaUuid}/questionnaire-submissions`,
  );
  return data?.data;
};

export const exchangePreviewToken = async (previewToken) => {
  const { data } = await api.post("/questionnaire-preview/session", {
    preview_token: previewToken,
  });
  return data?.data;
};

/**
 * Salvamento na saída da página. `sendBeacon` não serve: ele não permite o header Authorization,
 * e a API vive em outra origem.
 */
export const saveSectionOnUnload = (uuid, sectionId, body) => {
  const token = questionnaireAccessToken();

  return fetch(
    `${apiBaseOrigin}/questionnaire-submissions/${uuid}/sections/${sectionId}/answers`,
    {
      method: "PUT",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    },
  ).catch(() => undefined);
};
